import { FunctionComponent, useState } from 'react';
import styles from './FeedingComponent.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarLines } from '@fortawesome/pro-regular-svg-icons';
import { DrawerTooltip } from './DrawerTooltip';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import FeedingMomentContainer from './UI/FeedingMomentContainer';
import FeedingMoments from './UI/FeedingMoments';
import FeedingSpecificMoments from './UI/FeedingSpecificMoments';
import FoodProvisioner from './UI/FoodProvisioner';
import FoodType from './UI/FoodType';
import { Input } from 'components/UI/V3/Forms/Input/Input';
import FoodQuantity from './UI/FoodQuantity/FoodQuantity';
import clsx from 'clsx';
import { Feeding, FeedingScheduleType } from 'model/NewMultipleBooking';
import { SetStateAction } from 'react';

interface Props {
    feedingState: Feeding;
    disabled?: Array<number>;
    editing?: boolean;
    onFeedingChange: (arg: SetStateAction<Feeding>) => void;
}

export const FeedingComponent: FunctionComponent<Props> = ({ feedingState, disabled, editing, onFeedingChange }) => {
    const feedingScheduleType: FeedingScheduleType = feedingState.feedingScheduleType ?? 'feedingMoment';

    const handleScheduleTypeChange = (selected: boolean, newType: FeedingScheduleType) => {
        if (!selected) return;
        onFeedingChange((prev: Feeding) =>
            newType === 'feedingMoment'
                ? {
                      ...prev,
                      feedingScheduleType: 'feedingMoment',
                      specificMomentTimes: undefined
                  }
                : { ...prev, feedingScheduleType: newType }
        );
    };

    return (
        <div className={styles.feedingCard}>
            <div className={styles.cardContent}>
                <div className={styles.formSection}>
                    <div className={styles.sectionLabel}>
                        <FontAwesomeIcon
                            className={styles.icon}
                            icon={faCalendarLines}
                        />
                        Feeding Schedule <span className={styles.required}>*</span>
                        <DrawerTooltip
                            content="Set feeding times for each part of the day in Business Settings."
                            placement="top"
                        >
                            <FontAwesomeIcon
                                icon={faInfoCircle}
                                className={styles.infoIcon}
                            />
                        </DrawerTooltip>
                    </div>
                    <div className={styles.feedingScheduleContainer}>
                        <FeedingMomentContainer
                            selected={feedingScheduleType === 'feedingMoment'}
                            onSelectChange={selected => handleScheduleTypeChange(selected, 'feedingMoment')}
                            title="Feeding Moment"
                            description="Select a predefined time of day."
                        >
                            <div className={styles.checkboxGroup}>
                                <FeedingMoments
                                    disabled={disabled}
                                    editing={editing}
                                    selectedMoments={feedingState.momentTypes}
                                    onMomentsChange={(moments: Array<number>) =>
                                        onFeedingChange((prev: Feeding) => ({
                                            ...prev,
                                            momentTypes: moments
                                        }))
                                    }
                                />
                            </div>
                        </FeedingMomentContainer>
                    </div>
                    <div className={styles.feedingScheduleContainer}>
                        <FeedingMomentContainer
                            selected={feedingScheduleType === 'specificTime'}
                            onSelectChange={selected => handleScheduleTypeChange(selected, 'specificTime')}
                            title="Specific Time"
                            description="Set an exact feeding time."
                        >
                            <FeedingSpecificMoments
                                disabled={disabled}
                                editing={editing}
                                selectedMoments={feedingState.momentTypes ?? []}
                                momentTimes={feedingState.specificMomentTimes ?? {}}
                                onMomentsChange={(moments: Array<number>) =>
                                    onFeedingChange((prev: Feeding) => ({
                                        ...prev,
                                        momentTypes: moments
                                    }))
                                }
                                onMomentTimesChange={newMomentTimes =>
                                    onFeedingChange((prev: Feeding) => ({
                                        ...prev,
                                        specificMomentTimes: newMomentTimes
                                    }))
                                }
                            />
                        </FeedingMomentContainer>
                    </div>
                    <div className={styles.radioGroup}>
                        <FoodProvisioner
                            selectedProvisioner={feedingState.foodProvisionType}
                            onProvisionerChange={(provisioner: number) =>
                                onFeedingChange({ ...feedingState, foodProvisionType: provisioner })
                            }
                        />
                    </div>
                </div>

                <div className={clsx(styles.formSection, styles.foodTypeSection)}>
                    <div className={styles.sectionLeft}>
                        <FoodType
                            selectedOption={feedingState.foodType}
                            onSelect={(foodType: number) => onFeedingChange({ ...feedingState, foodType: foodType })}
                        />
                    </div>
                    <div className={styles.sectionRight}>
                        <FoodQuantity
                            selectedQuantity={feedingState.quantity.quantity}
                            selectedUnit={feedingState.quantity.unit}
                            onQuantityChange={(quantity: number, unit: number) =>
                                onFeedingChange({
                                    ...feedingState,
                                    quantity: { ...feedingState.quantity, quantity: quantity, unit: unit }
                                })
                            }
                        />
                    </div>
                </div>
                {feedingState.foodProvisionType === 2 && (
                    <div className={styles.formSection}>
                        <Input
                            label="Brand"
                            className={styles.instructions}
                            value={feedingState.brand || ''}
                            onChange={e => onFeedingChange({ ...feedingState, brand: e.target.value })}
                            placeholder="Ex. Royal Canin, Farmer's Dog, Hil..."
                        />
                    </div>
                )}
                <div className={styles.formSection}>
                    <Input
                        label="Feeding Instructions"
                        className={styles.instructions}
                        value={feedingState.instructions || ''}
                        onChange={e => onFeedingChange({ ...feedingState, instructions: e.target.value })}
                        placeholder="Ex. Feed separately from other pets"
                    />
                </div>
            </div>
        </div>
    );
};

export default FeedingComponent;
